import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { SECTIONS, TOTAL_QUESTIONS } from "@/data/questions";
import { createAssessment } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const PROFILE_KEYS = ["business_name", "industry", "annual_revenue", "employee_count", "avg_transaction_value", "website", "google_business_url"];

export default function Assessment() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const section = SECTIONS[stepIdx];
  const isLastSection = stepIdx === SECTIONS.length - 1;
  const answeredCount = useMemo(() => Object.keys(answers).filter((k) => answers[k] !== "" && answers[k] !== undefined).length, [answers]);
  const overallProgress = Math.round((answeredCount / TOTAL_QUESTIONS) * 100);

  const setAnswer = (qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }));

  const requiredInSection = section.questions.filter((q) => q.required);
  const sectionComplete = requiredInSection.every((q) => {
    const v = answers[q.id];
    return v !== undefined && v !== "";
  });

  const handleNext = async () => {
    if (!sectionComplete) {
      toast.error("Please answer all required questions in this section.");
      return;
    }
    if (!isLastSection) {
      setStepIdx((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const profile = {};
      PROFILE_KEYS.forEach((k) => { profile[k] = answers[k] || ""; });
      const payload = {
        business_name: profile.business_name,
        industry: profile.industry,
        annual_revenue: profile.annual_revenue,
        employee_count: profile.employee_count,
        avg_transaction_value: profile.avg_transaction_value,
        website: profile.website,
        google_business_url: profile.google_business_url,
        answers: { ...answers },
      };
      const res = await createAssessment(payload);
      navigate(`/report/${res.assessment_id}`);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error(e);
      }
      toast.error("Could not submit your assessment. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      <header className="border-b border-gold/10 sticky top-0 bg-navy-900/95 backdrop-blur z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="back-home-button"><Logo size="sm" /></button>
          <div className="text-xs text-ink-muted tabular-nums" data-testid="progress-label">{answeredCount} / {TOTAL_QUESTIONS} answered</div>
        </div>
        <div className="h-px bg-navy-700 relative">
          <div className="absolute inset-y-0 left-0 bg-gold transition-all duration-500" style={{ width: `${overallProgress}%` }} data-testid="progress-bar" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-12 gap-10">
        <aside className="md:col-span-3 md:sticky md:top-32 md:self-start">
          <div className="overline text-gold mb-4">Audit Sections</div>
          <ol className="space-y-3">
            {SECTIONS.map((s, i) => {
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => i <= stepIdx && setStepIdx(i)}
                    data-testid={`section-nav-${s.id}`}
                    className={`w-full text-left flex items-start gap-3 py-2 ${i > stepIdx ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <span className={`mt-0.5 h-5 w-5 flex items-center justify-center border ${active ? "border-gold bg-gold text-navy-900" : done ? "border-ok text-ok" : "border-ink-muted/30 text-ink-muted"} text-[10px] tabular-nums font-semibold`}>
                      {done ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <span className={`text-sm leading-tight ${active ? "text-ink" : "text-ink-muted"}`}>{s.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <section className="md:col-span-9" key={section.id}>
          <div className="animate-fade-up">
            <div className="overline text-gold mb-3">{section.overline}</div>
            <h1 className="font-display text-4xl md:text-5xl font-light mb-3" data-testid="section-title">{section.title}</h1>
            <p className="text-ink-muted text-lg max-w-2xl mb-12">{section.description}</p>

            <div className="space-y-10">
              {section.questions.map((q) => (
                <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              ))}
            </div>

            <div className="gold-hairline my-12" />
            <div className="flex items-center justify-between">
              <button
                disabled={stepIdx === 0 || submitting}
                onClick={() => { setStepIdx((i) => Math.max(0, i - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                data-testid="back-section-button"
                className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4" /> Previous
              </button>
              <button
                onClick={handleNext}
                disabled={submitting}
                data-testid="next-section-button"
                className="inline-flex items-center gap-3 bg-gold text-navy-900 px-7 py-3.5 font-semibold hover:bg-gold-400 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Submitting…" : isLastSection ? "See My AI Readiness Score" : "Continue"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
            {isLastSection && (
              <p className="mt-6 text-xs text-ink-muted leading-relaxed text-right" data-testid="assessment-consent">
                By submitting this audit you agree to our{" "}
                <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>
                {" "}and{" "}
                <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "text") {
    return (
      <div data-testid={`question-${q.id}`}>
        <label className="block font-display text-xl mb-3">{q.label}{q.required && <span className="text-gold ml-1">*</span>}</label>
        <input
          type="text"
          placeholder={q.placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className="w-full bg-navy-800 border border-gold/15 px-4 py-3 text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-gold transition-colors"
        />
      </div>
    );
  }
  if (q.type === "select") {
    return (
      <div data-testid={`question-${q.id}`}>
        <label className="block font-display text-xl mb-3">{q.label}{q.required && <span className="text-gold ml-1">*</span>}</label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className="w-full bg-navy-800 border border-gold/15 px-4 py-3 text-ink focus:outline-none focus:border-gold transition-colors"
        >
          <option value="">Select…</option>
          {q.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div data-testid={`question-${q.id}`}>
      <label className="block font-display text-xl mb-4">{q.label}{q.required && <span className="text-gold ml-1">*</span>}</label>
      <div className="grid gap-2">
        {q.options.map(([v, l]) => {
          const active = value === v;
          return (
            <button
              type="button"
              key={v}
              onClick={() => onChange(v)}
              data-testid={`option-${q.id}-${v}`}
              className={`text-left px-5 py-3.5 border transition-all flex items-center gap-4 ${active ? "border-gold bg-gold/10" : "border-gold/15 bg-navy-800 hover:border-gold/40"}`}
            >
              <span className={`h-4 w-4 border flex items-center justify-center ${active ? "border-gold" : "border-ink-muted/40"}`}>
                {active && <span className="h-2 w-2 bg-gold" />}
              </span>
              <span className="text-ink">{l}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
